import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { useFocusEffect } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry } from 'features/cart/types';
import { isEmpty } from 'lodash-es';
import { FlatList, StyleSheet, View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import ConfettiFriesAnimation from 'components/lottie-animations/confetti-fries';
import { useConfettiFriesAnimation } from 'components/lottie-animations/confetti-fries/use-confetti-fries-animation';
import {
  PullToReloadScrollView,
  pullToReloadStyles,
  usePullToReload,
} from 'components/pull-to-reload/pull-to-reload';
import { toaster } from 'components/toaster/toaster';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import OffersEmptyScreen from 'pages/loyalty/loyalty-offers/empty-screen/empty-screen';
import EndOfOffers from 'pages/loyalty/loyalty-offers/end-of-offers/loyalty-end-of-offers';
import { LoyaltyListItem } from 'pages/loyalty/loyalty-offers/loyalty-list-item';
import { LoyaltyOffersSkeletonLoader } from 'pages/loyalty/loyalty-offers/loyalty-offers-skeleton-loader';
import { LoyaltyPromoCodeCTA } from 'pages/loyalty/loyalty-offers/loyalty-promo-code/loyalty-promo-code-cta';
import { LoyaltyPromoCodeDrawer } from 'pages/loyalty/loyalty-offers/loyalty-promo-code/loyalty-promo-code-drawer';
import { PromoCodeRedeemSuccessParam } from 'pages/loyalty/loyalty-offers/loyalty-promo-code/types';
import { OffersInCart } from 'pages/loyalty/loyalty-offers/offers-in-cart/offers-in-cart';
import useLoyaltyOffersFlow from 'pages/loyalty/loyalty-offers/use-loyalty-offers-flow';
import {
  ClickEventComponentNames,
  CustomEventNames,
  logButtonClick,
  logLoyaltyEvent,
} from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger } from 'state/auth/types';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { LoyaltyOfferWithInlineAlertMessage } from 'state/loyalty/hooks/types';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import {
  AppOfferListItem,
  EnginePersonalizedOffer,
  LoyaltyOffer,
  SanityConfigOffer,
  appOfferToLoyaltyOffer,
  buildAppPersonalizedOffer,
  isAppOffer,
} from 'state/loyalty/types';
import { isDiscountLoyaltyOffer } from 'state/loyalty/utils';
import { spacing } from 'styles/constants';
import { TRANSITION_ANIMATION_TIME_MS, WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { routes } from 'utils/routing';
import { stringToBool } from 'utils/string-To-Bool';
import { waitForDuration } from 'utils/wait-for-duration';

import { useLoyaltyServiceOffers } from '../hooks/use-loyalty-service-offers';
import { useServiceIncentive } from '../hooks/use-service-incentive';

export const OffersBase = () => {
  const { loyaltyStandardOffersEnabled } = useLoyaltyContext();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { setModalAuthState } = useAuthContext();
  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);
  const dispatch = useAppDispatch();
  const {
    inRestaurantRedemptionCart,
    removeInRestaurantRedemptionEntry,
  } = useInRestaurantRedemptionContext();
  const [authRequested, setAuthRequested] = useState(false);

  const enableLoyaltyPromoCodes = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROMO_CODES);
  const { removeFromCart } = useCart();
  const { removeItemFromMinimalCart } = useMinimalCart();

  const router = useRouter();

  const { loyaltyEngineId } = useLoyaltyOffersFlow();
  const skipAnimation = useRef(true);

  const { triggerAnimation } = useConfettiFriesAnimation();
  const queryParams = useLocalSearchParams<{ success?: string; promoCode?: string }>();
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(
    !!stringToBool(queryParams.success)
  );

  const [isPromoCodeOpen, _setIsPromoCodeOpen] = useState(false);

  const openPromoCodeDrawer = useCallback(() => {
    setTimeout(() => {
      _setIsPromoCodeOpen(true);
    }, TRANSITION_ANIMATION_TIME_MS);
  }, []);

  const closePromoCodeDrawer = useCallback(() => {
    _setIsPromoCodeOpen(false);
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (queryParams.promoCode && !authRequested) {
        openPromoCodeDrawer();
      }
    }, [openPromoCodeDrawer, queryParams.promoCode, authRequested])
  );

  const shouldDisplayPromoCodeDrawer = useRef(false);

  useLoyaltyUserState({
    refreshOnMountOrFocus: true,
    onSuccess: () => {
      if (shouldDisplayPromoCodeDrawer.current) {
        openPromoCodeDrawer();
        shouldDisplayPromoCodeDrawer.current = false;
      }
    },
  });

  const onDismissPromoDrawer = useCallback(() => {
    router.setParams({ promoCode: '' });
    closePromoCodeDrawer();
  }, [closePromoCodeDrawer, router]);

  const handleAuthenticationRequired = (promoCode: string) => {
    router.setParams({ promoCode });
    setAuthRequested(true);
    closePromoCodeDrawer();

    setTimeout(() => {
      setModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
        trigger: ModalAuthTrigger.Global,
        onClose: () => {
          setAuthRequested(false);
          openPromoCodeDrawer();
        },
        onSignInSuccess: () => {
          shouldDisplayPromoCodeDrawer.current = true;
        },
      });
    });
  };

  const {
    offerListItems,
    offersInCart,
    personalizedOfferGroups,
    reprocessOffers,
    refreshOffers,
    loading: offersAreLoading,
  } = useLoyaltyServiceOffers();

  useEffect(() => {
    dispatch(actions.loyalty.setAppPersonalizedOfferGroups(personalizedOfferGroups));
  }, [dispatch, personalizedOfferGroups]);

  const { selectedIncentive } = useServiceIncentive({
    loyaltyEngineId,
    offerListItems,
    offersInCart,
  });

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const removeLoyaltyOffer = useCallback(
    (offerToRemove: LoyaltyOffer) => {
      const appliedOfferToRemove = appliedOffers.find(
        offer => offer.id === offerToRemove.loyaltyEngineId
      );

      if (appliedOfferToRemove?.cartId) {
        dispatch(actions.loyalty.removeAppliedOffer(appliedOfferToRemove));
        removeFromCart({ cartId: appliedOfferToRemove.cartId });
        return true;
      }

      return false;
    },
    [appliedOffers, dispatch, removeFromCart]
  );

  const handleRemove = useCallback(
    (offerToRemove: LoyaltyOffer) => {
      const redemptionOfferToRemove = inRestaurantRedemptionCart.find(
        cartEntry =>
          cartEntry.type === ICartEntryType.OFFER &&
          cartEntry.details.offer._id === offerToRemove._id
      );

      let showNotification = false;

      if (redemptionOfferToRemove) {
        removeInRestaurantRedemptionEntry(redemptionOfferToRemove);
        showNotification = true;
      } else if (isDiscountLoyaltyOffer(offerToRemove)) {
        if (isUnifiedMenuEnabled) {
          removeItemFromMinimalCart({ cartId: DISCOUNT_OFFER_CART_ID } as ICartEntry);
        }
        removeFromCart({ cartId: DISCOUNT_OFFER_CART_ID });
        dispatch(actions.loyalty.setSelectedOffer(null));
        dispatch(actions.loyalty.resetAppliedDiscountOffers());
        reprocessOffers();
        showNotification = true;
      } else {
        reprocessOffers();
        showNotification = removeLoyaltyOffer(offerToRemove);
      }

      if (showNotification) {
        toaster.positive(t('offerAppliedSuccess'));
      }
    },
    [
      inRestaurantRedemptionCart,
      removeInRestaurantRedemptionEntry,
      isUnifiedMenuEnabled,
      removeFromCart,
      dispatch,
      reprocessOffers,
      removeItemFromMinimalCart,
      removeLoyaltyOffer,
    ]
  );

  useEffect(() => {
    if (shouldRefetchOffers) {
      refreshOffers();
    }
  }, [refreshOffers, shouldRefetchOffers]);

  useEffect(() => {
    if (offersInCart?.length && !offerListItems.length) {
      dispatch(actions.loyalty.setShouldRefetchOffers(true));
    }
  }, [offersInCart?.length, offerListItems.length, dispatch]);

  const handleOfferPress = useCallback(
    (incentive: LoyaltyOfferWithInlineAlertMessage) => {
      if (incentive.inlineAlertMessage) {
        return;
      }
      const incentiveId = incentive._id;
      const loyaltyEngineId = incentive.loyaltyEngineId;
      if (incentiveId !== selectedIncentive?._id) {
        logLoyaltyEvent({
          name: CustomEventNames.OFFER_SELECTED,
          incentives: [incentive],
          attributes: {
            serviceMode: incentive.offerServiceMode,
          },
        });
        router.navigate(`${routes.loyaltyOfferList}/${loyaltyEngineId}`);
      }
    },
    [router, selectedIncentive]
  );

  const handleGroupPress = useCallback(
    (offerGroup: { cmsId: string; id?: string }) => {
      const qs = offerGroup.id ? `?loyaltyEngineId=${offerGroup.id}` : '';
      router.navigate(`${routes.loyaltyOfferGroup}/${offerGroup.cmsId}${qs}`);
    },
    [router]
  );

  useEffect(() => {
    if (offerListItems.length > 0 && !offersAreLoading) {
      const onlyOffers = offerListItems.filter(isAppOffer).map(appOfferToLoyaltyOffer);
      logLoyaltyEvent(
        {
          name: CustomEventNames.OFFERS_DISPLAYED,
          incentives: onlyOffers,
          attributes: {
            offerCount: onlyOffers.length,
          },
        },
        { skipLoggingToBraze: true }
      );
    }
  }, [offerListItems, offersAreLoading]);

  useRumPageView(
    `loyalty-offers${selectedIncentive ? '-detail' : '-list'}`,
    `Loyalty Offers${selectedIncentive ? ' Detail' : ' List'}`
  );

  useEffect(() => {
    if (
      loyaltyEngineId &&
      !selectedIncentive &&
      loyaltyStandardOffersEnabled &&
      !offersAreLoading &&
      !isEmpty(offerListItems)
    ) {
      toaster.negative(t('incentiveFeedbackInvalidStore', { incentiveType: t('offer') }));
    }
  }, [
    offerListItems,
    offersAreLoading,
    selectedIncentive,
    loyaltyEngineId,
    loyaltyStandardOffersEnabled,
  ]);

  const renderItem = useCallback(
    ({ item }: { item: AppOfferListItem }) => {
      return (
        <LoyaltyListItem
          item={item}
          handleOfferPress={handleOfferPress}
          handleGroupPress={handleGroupPress}
          skipAnimation={skipAnimation.current}
        />
      );
    },
    [handleOfferPress, handleGroupPress]
  );

  const onPromoCodeRedeemSuccess = useCallback(
    ({ configOffer, engineOffer }: PromoCodeRedeemSuccessParam) => {
      skipAnimation.current = false;
      toaster.positive(t('promoCodeAddedToOffers'));
      dispatch(actions.loyalty.setCmsOffers([configOffer as SanityConfigOffer]));
      const appPersonalizedOffer = buildAppPersonalizedOffer(
        configOffer as SanityConfigOffer,
        engineOffer as EnginePersonalizedOffer
      );
      dispatch(actions.loyalty.unshiftAppPersonalizedOffer(appPersonalizedOffer));

      router.navigate(
        routes.loyaltyOfferDetails.replace(':loyaltyEngineId', appPersonalizedOffer.loyaltyEngineId)
      );
    },
    [dispatch, router]
  );

  const canShowPromoCode = enableLoyaltyPromoCodes && !selectedIncentive;

  const ListHeaderComponent = useCallback(() => {
    const onOpenPromoDrawer = () => {
      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PROMO_CODE_TILE,
          name: 'Have a Promo Code?',
        },
      });
      openPromoCodeDrawer();
    };

    return (
      <>
        {canShowPromoCode && <LoyaltyPromoCodeCTA onPress={onOpenPromoDrawer} />}
        {offersInCart?.length ? (
          <OffersInCart
            offers={offersInCart}
            handlePress={handleOfferPress}
            handleRemove={handleRemove}
          />
        ) : null}
      </>
    );
  }, [canShowPromoCode, handleOfferPress, handleRemove, offersInCart, openPromoCodeDrawer]);

  const onRefresh = async () => {
    await waitForDuration(1700);
    refreshOffers();
  };

  const { pullToReloadSpinner, pullToReloadProps } = usePullToReload({
    progressViewOffset: 10,
    onRefresh,
  });

  useEffect(() => {
    if (showSuccessAnimation && loyaltyStandardOffersEnabled && !offersAreLoading) {
      triggerAnimation();
      toaster.positive(t('walmartEnjoyYourOffers'));
      setShowSuccessAnimation(false);
      router.setParams({ success: undefined });
    }
  }, [
    loyaltyStandardOffersEnabled,
    router,
    showSuccessAnimation,
    offersAreLoading,
    triggerAnimation,
  ]);

  if (!loyaltyStandardOffersEnabled) {
    return null;
  }

  if (offersAreLoading) {
    return <LoyaltyOffersSkeletonLoader />;
  }

  return (
    <>
      {offerListItems?.length ? (
        <>
          <View style={pullToReloadStyles.scrollViewContainer}>
            {pullToReloadSpinner}
            <FlatList
              scrollEventThrottle={16}
              ListHeaderComponent={ListHeaderComponent}
              ListFooterComponent={EndOfOffers}
              contentContainerStyle={styles.flatList}
              data={offerListItems}
              initialNumToRender={5}
              keyExtractor={(item, index) => {
                if (isAppOffer(item)) {
                  return item.loyaltyEngineId;
                }
                if ('cmsId' in item && item.cmsId) {
                  return item.cmsId;
                }
                return `${index}`;
              }}
              renderItem={renderItem}
              {...pullToReloadProps}
            />
          </View>
          <ConfettiFriesAnimation />
        </>
      ) : (
        <PullToReloadScrollView onRefresh={onRefresh} progressViewOffset={10}>
          <OffersEmptyScreen />
        </PullToReloadScrollView>
      )}
      {isPromoCodeOpen && (
        <LoyaltyPromoCodeDrawer
          isOpen={isPromoCodeOpen}
          onDismiss={onDismissPromoDrawer}
          promoCode={queryParams.promoCode?.toString()}
          onPromoCodeRedeemSuccess={onPromoCodeRedeemSuccess}
          onAuthenticationRequired={handleAuthenticationRequired}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  flatList: {
    ...centeredRowContainerStyles.content,
    maxWidth: WEB_SMALL_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
  },
});
